import { isClientErrorsEnabled } from "../helpers/clientErrorsEnabled";

/**
 * Sends crash and problem reports to the backend (POST /client-errors). Never throws and never shows
 * anything: a report that fails is simply dropped. Text is scrubbed here and again on the server, so a
 * phone number, email, token or ID number on screen never leaves the device.
 */

export type ClientErrorKind = "CRASH" | "ERROR" | "REJECTION" | "REPORT";

/** Automatic reports per page load; a crash loop stops here. */
export const MAX_AUTO_REPORTS = 10;
/** "Report a problem" sends per page load. */
export const MAX_USER_REPORTS = 3;
const RECENT_SIZE = 5;

const EMAIL = /[\w.+-]+@[\w-]+(\.[\w-]+)+/g;
const JWT = /eyJ[\w-]+\.[\w-]+\.[\w-]*/g;
const BEARER = /bearer\s+\S+/gi;
const URL_QUERY = /(https?:\/\/[^\s?#"']+)[?#][^\s"']*/g;
const PATH_QUERY = /(\/[^\s?#"']*)\?[^\s"']*/g;
const SECRET_PARAM = /\b(token|access_token|refresh_token|key|apikey|secret|confirm|code|otp|password|pin|sig|signature)=[^\s&"']+/gi;
/** Page names ("product-certification-new1", "createProduct"); anything else in a path may be a secret. */
const WORD_SEGMENT = /^[a-z][a-zA-Z]{0,39}(-[a-zA-Z0-9]{1,20}){0,5}$/;
const HEX_SEGMENT = /^[0-9a-fA-F]{24}$/;
/** Public links whose next segment is the secret (/capture/:token, /p/:token, /w/:code, ...). */
const SECRET_PREFIXES = new Set(["p", "q", "w", "a", "order-approval", "capture", "partner-invite", "open-piece", "confirm"]);
/** The same secret links inside free text: the segment after the prefix is masked. Needs a leading "/". */
const SECRET_PATH = /\/(p|q|w|a|order-approval|capture|partner-invite|open-piece|confirm)\/[^/\s?#"'\\]+/g;
const DIGIT_RUN = /\+?\d[\d \-]{4,}\d/g;
const HEX_ID = /[0-9a-fA-F]{24}/g;

let autoSent = 0;
let userSent = 0;
const seen = new Set<string>();
const recent: string[] = [];

const insideHexId = (text: string, start: number, end: number): boolean => {
  for (const m of Array.from(text.matchAll(HEX_ID))) {
    const s = m.index ?? 0;
    if (s <= start && s + m[0].length >= end) return true;
  }
  return false;
};

export const scrub = (value: unknown, max: number): string => {
  if (value === null || value === undefined) return "";
  let s = String(value)
    .replace(SECRET_PATH, "/$1/:x")
    .replace(EMAIL, "[email]")
    .replace(JWT, "[token]")
    .replace(BEARER, "Bearer [token]")
    .replace(URL_QUERY, "$1")
    .replace(PATH_QUERY, "$1")
    .replace(SECRET_PARAM, "$1=[x]");
  s = s.replace(DIGIT_RUN, (run, offset: number, whole: string) => {
    const digits = run.replace(/\D/g, "").length;
    return digits >= 6 && !insideHexId(whole, offset, offset + run.length) ? "[number]" : run;
  });
  s = s.trim();
  return s.length > max ? s.slice(0, max) : s;
};

/**
 * The page path with every segment that is not a page name or a 24-hex id replaced by ":x": several
 * public links carry their secret in the path. Never the query string. The server does this again.
 */
export const scrubRoute = (pathname: string): string => {
  let afterSecret = false;
  return pathname
    .split("/")
    .map((seg) => {
      if (!seg) return seg;
      const keep = !afterSecret && (WORD_SEGMENT.test(seg) || HEX_SEGMENT.test(seg));
      afterSecret = SECRET_PREFIXES.has(seg);
      return keep ? seg : ":x";
    })
    .join("/")
    .slice(0, 300);
};

/** True when a segment of the path follows a secret-link prefix (/a/<token>, /orders/confirm/<token>, ...). */
export const hasSecretPath = (pathname: string): boolean => {
  const segs = pathname.split("/").filter(Boolean);
  return segs.some((seg, i) => SECRET_PREFIXES.has(seg) && i < segs.length - 1);
};

export const currentRoute = (): string => scrubRoute(window.location.pathname);

/** The main bundle's build hash identifies the deploy without any build config. */
export const appVersion = (): string => {
  const script = document.querySelector<HTMLScriptElement>('script[src*="/static/js/main."]');
  const match = script?.src.match(/main\.([0-9a-f]+)\./);
  return match ? match[1] : "dev";
};

const authHeader = (): Record<string, string> => {
  try {
    const token = localStorage.getItem("token");
    return token && token !== "null" && token !== "undefined" ? { Authorization: "Bearer " + token } : {};
  } catch {
    return {};
  }
};

const send = (body: Record<string, unknown>): Promise<string | null> => {
  const base = (process.env.REACT_APP_SERVERURL || "").replace(/\/$/, "");
  return fetch(`${base}/client-errors`, {
    method: "POST",
    keepalive: true,
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify(body),
  })
    .then(async (res) => {
      if (!res.ok) return null;
      const json = await res.json().catch(() => null);
      return json?.data?.reference || null;
    })
    .catch(() => null);
};

const messageOf = (error: unknown): string => {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

/** Errors the browser reports that say nothing useful or are harmless. */
const isNoise = (message: string): boolean =>
  /ResizeObserver loop|^Script error\.?$|Non-Error promise rejection captured/i.test(message);

/** Crash or uncaught error: reported automatically, once per distinct message, capped per page load. */
export const reportError = (kind: Exclude<ClientErrorKind, "REPORT">, error: unknown, extraStack?: string): void => {
  try {
    const message = scrub(messageOf(error), 500);
    if (!message || isNoise(message)) return;
    recent.push(`${kind}: ${message}`.slice(0, 300));
    if (recent.length > RECENT_SIZE) recent.shift();
    if (!isClientErrorsEnabled() || autoSent >= MAX_AUTO_REPORTS || seen.has(message)) return;
    seen.add(message);
    autoSent += 1;
    const stack = [error instanceof Error ? error.stack : "", extraStack || ""].filter(Boolean).join("\n");
    void send({
      kind,
      message,
      stack: scrub(stack, 4000),
      route: currentRoute(),
      appVersion: appVersion(),
      userAgent: scrub(navigator.userAgent, 300),
    });
  } catch {
    // Reporting must never add a second failure.
  }
};

/** "Report a problem": the user's note plus the last few errors seen. Resolves to a reference, or null. */
export const sendProblemReport = (note: string): Promise<string | null> => {
  if (!isClientErrorsEnabled() || userSent >= MAX_USER_REPORTS) return Promise.resolve(null);
  // Only a report that arrived counts, so going offline or a refused send does not use up a try.
  return send({
    kind: "REPORT",
    message: "User report",
    note: scrub(note, 500),
    recent: recent.map((r) => scrub(r, 300)),
    route: currentRoute(),
    appVersion: appVersion(),
    userAgent: scrub(navigator.userAgent, 300),
  }).then((reference) => {
    if (reference) userSent += 1;
    return reference;
  });
};

let installed = false;

/** Reports uncaught errors and unhandled promise rejections anywhere in the app. */
export const installGlobalErrorReporting = (): void => {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (event: ErrorEvent) => {
    reportError("ERROR", event.error || event.message);
  });
  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    reportError("REJECTION", event.reason);
  });
};

/** Tests only. */
export const resetClientErrorReporterForTests = (): void => {
  autoSent = 0;
  userSent = 0;
  seen.clear();
  recent.length = 0;
};
